import * as React from 'react';
import { Bot, RefreshCw, ArrowLeft } from 'lucide-react';
import apiService from '../../services/api';
import {
  executeSkillInsertions,
  getDocumentParagraphsForSkill,
  repositionSkillInsertions,
} from '../../features/agent/skills/SkillWordExecutor';
import {
  localizeSkillMeta,
  SKILL_AGENT_LANGUAGE,
} from '../../features/agent/skills/builtInSkills';
import { getSkillRunnerCopy } from '../../features/agent/skills/skillRunnerCopy';
import { getStoredUserDisplayName } from '../../features/agent/skills/skillAuthor';
import { logError } from '../../utils/logging';
import type {
  AgentSkillMeta,
  SkillAnalyzeResult,
  SkillChatTurn,
  SkillExecuteResult,
  SkillGroupAssignment,
  SkillOrderMode,
  SkillOrderingContext,
  SkillProposeStrategyResult,
  SkillSessionState,
  SkillStrategyGroup,
} from '../../features/agent/skills/types';
import type { SkillRunnerCopy } from '../../features/agent/skills/skillRunnerCopy';
import { ChatComposer } from './ChatComposer';
import './SkillRunner.css';

function shouldOfferOuterClauseMatch(ctx: SkillOrderingContext | undefined): boolean {
  if (!ctx?.clarification_message?.trim()) return false;
  if (ctx.needs_ordering_clarification) return true;
  return !!(ctx.pasted_generic_numbering && ctx.document_has_outer_clause_refs);
}

/** One wizard question's body text — a citation group with a signal (e.g.
 * "לנספח א'") reads differently from the catch-all group of general requests
 * that cite nothing (signal === ""). */
function wizardQuestionText(copy: SkillRunnerCopy, group: SkillStrategyGroup): string {
  return group.signal
    ? copy.wizardQuestion(group.count, group.signal, group.rationale)
    : copy.wizardQuestionNoSignal(group.count, group.rationale);
}

let _turnId = 0;
function nextTurnId(): string {
  return `sk-turn-${++_turnId}-${Date.now()}`;
}

function makeInitialState(skill: AgentSkillMeta): SkillSessionState {
  const copy = getSkillRunnerCopy(SKILL_AGENT_LANGUAGE);
  const welcome: SkillChatTurn = {
    id: nextTurnId(),
    role: 'assistant',
    content: copy.welcomePaste,
  };
  return {
    skill,
    step: 'awaiting_paste',
    turns: [welcome],
    pastedText: '',
    analyzeResult: null,
    selectedLocationIndex: null,
    selectedLocationLabel: null,
    chronological: null,
    orderMode: 'pasted',
    formatMode: null,
    author: copy.defaultAuthor,
    executeResult: null,
    strategyResult: null,
    wizard: null,
    error: null,
  };
}

interface SkillRunnerProps {
  skill: AgentSkillMeta;
  onBack: () => void;
}

export const SkillRunner: React.FC<SkillRunnerProps> = ({ skill, onBack }) => {
  const displaySkill = React.useMemo(() => localizeSkillMeta(skill), [skill]);
  const copy = React.useMemo(() => getSkillRunnerCopy(SKILL_AGENT_LANGUAGE), []);

  const [session, setSession] = React.useState<SkillSessionState>(() => makeInitialState(skill));
  const [input, setInput] = React.useState('');
  const [newHeadingInput, setNewHeadingInput] = React.useState('');
  const [newHeadingError, setNewHeadingError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [profileAuthorName, setProfileAuthorName] = React.useState(() => getStoredUserDisplayName());
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const executingRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getCurrentUser();
        if (!cancelled && res.success && res.data) {
          const name = (res.data.full_name || res.data.name || '').trim();
          if (name) {
            setProfileAuthorName(name);
            return;
          }
        }
      } catch {
        /* use stored user */
      }
      if (!cancelled) setProfileAuthorName(getStoredUserDisplayName());
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    setSession(makeInitialState(skill));
    setInput('');
    setNewHeadingInput('');
    setNewHeadingError(null);
  }, [skill.id]);

  React.useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [session.turns, session.step]);

  const appendTurn = (role: 'user' | 'assistant', content: string) => {
    setSession((s) => ({
      ...s,
      turns: [...s.turns, { id: nextTurnId(), role, content }],
    }));
  };

  const handlePasteSubmit = async (text: string) => {
    const pasted = text.trim();
    if (!pasted) {
      setSession((s) => ({ ...s, error: copy.errorEmptyPaste }));
      return;
    }
    setBusy(true);
    setSession((s) => ({ ...s, error: null, pastedText: pasted }));
    appendTurn('user', pasted);

    try {
      const paras = await getDocumentParagraphsForSkill();

      const res = await apiService.analyzeAgentSkill({
        skillId: skill.id,
        pastedText: pasted,
        paragraphs: paras,
        language: SKILL_AGENT_LANGUAGE,
      });

      if (!res.success || !res.data) {
        throw new Error(res.message || 'Analysis failed');
      }

      const analyzeResult: SkillAnalyzeResult = {
        client_requests: (res.data.client_requests || []).map((cr) => ({
          text: cr.text,
          clause_number: cr.clause_number || '',
        })),
        location_options: res.data.location_options || [],
        has_clause_numbers: !!res.data.has_clause_numbers,
        ordering_context: res.data.ordering_context,
      };

      const count = analyzeResult.client_requests.length;
      appendTurn('assistant', copy.foundRequests(count));

      setSession((s) => ({
        ...s,
        step: 'awaiting_location',
        analyzeResult,
        error: null,
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Analysis failed';
      setSession((s) => ({ ...s, error: msg }));
      appendTurn('assistant', copy.errorAnalyze(msg));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Upfront wizard for auto mode: many real pastes cite appendices (לנספח א' /
   * להסכם / נספח הלוואת הקבלן / …) that are never named in this document's own
   * headings, so the placement-strategy loop's grouping is a genuine inference
   * per citation group — propose ALL groups in one call (each with its own
   * grounded rationale), then walk the lawyer through one question per group,
   * client-side, BEFORE inserting a single paragraph.
   */
  const proposeStrategy = async () => {
    if (!session.analyzeResult) return;
    setBusy(true);
    appendTurn('assistant', copy.analyzingStrategy);
    try {
      const paras = await getDocumentParagraphsForSkill();
      const res = await apiService.proposeAutoSkillStrategy({
        skillId: skill.id,
        clientRequests: session.analyzeResult.client_requests,
        paragraphs: paras,
        locationOptions: session.analyzeResult.location_options,
        language: SKILL_AGENT_LANGUAGE,
      });
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Strategy proposal failed');
      }
      const strategyResult: SkillProposeStrategyResult = {
        groups: res.data.groups,
        available_headings: res.data.available_headings,
        strategy_trace: res.data.strategy_trace,
      };

      if (strategyResult.groups.length === 0) {
        setSession((s) => ({ ...s, strategyResult, step: 'awaiting_format', error: null }));
        appendTurn('assistant', copy.askFormat);
        return;
      }

      // The intro is the only wizard-related chat turn posted up front — every
      // question after this renders inside a dedicated step-through card (see
      // the render block below), not as its own growing pair of chat turns.
      appendTurn('assistant', copy.wizardIntro(strategyResult.groups.length));
      setSession((s) => ({
        ...s,
        strategyResult,
        wizard: {
          groups: strategyResult.groups,
          currentIndex: 0,
          assignments: [],
          availableHeadings: strategyResult.available_headings,
          awaitingNewHeadingText: false,
        },
        step: 'awaiting_wizard_question',
        error: null,
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Strategy proposal failed';
      appendTurn('assistant', copy.errorProposeStrategy(msg));
      setSession((s) => ({ ...s, step: 'done', error: msg }));
    } finally {
      setBusy(false);
    }
  };

  const handleAutoLocationSelect = () => {
    appendTurn('user', copy.decideYourself);
    setSession((s) => ({ ...s, chronological: false, orderMode: 'auto', step: 'proposing_strategy' }));
    void proposeStrategy();
  };

  /** Records the current group's assignment and either steps the wizard card
   * to the next question or, once every group has an answer, posts a single
   * decisions-summary chat turn and moves on to format selection. Nothing
   * here touches the chat transcript per-question — see the wizard card
   * render block for why (the whole point of this redesign). */
  const advanceWizard = (assignment: SkillGroupAssignment) => {
    const wizard = session.wizard;
    if (!wizard) return;
    const assignments = [...wizard.assignments, assignment];
    const nextIndex = wizard.currentIndex + 1;

    if (nextIndex < wizard.groups.length) {
      setSession((s) =>
        s.wizard
          ? { ...s, wizard: { ...s.wizard, assignments, currentIndex: nextIndex, awaitingNewHeadingText: false } }
          : s,
      );
      return;
    }

    const summaryLines = wizard.groups.map((g, i) =>
      copy.wizardDecisionLine(g.count, g.signal || copy.generalGroupLabel, assignments[i]?.label ?? ''),
    );
    appendTurn('assistant', `${copy.wizardSummaryHeader}\n${summaryLines.join('\n')}`);
    appendTurn('assistant', copy.wizardAllAnswered);
    appendTurn('assistant', copy.askFormat);
    setSession((s) =>
      s.wizard
        ? { ...s, wizard: { ...s.wizard, assignments, awaitingNewHeadingText: false }, step: 'awaiting_format' }
        : s,
    );
  };

  const handleWizardHeadingChoice = (paragraphIndex: number, label: string, anchorText: string) => {
    if (!session.wizard) return;
    const group = session.wizard.groups[session.wizard.currentIndex];
    if (!group) return;
    advanceWizard({
      request_indices: group.request_indices,
      target_paragraph_index: paragraphIndex,
      anchor_text: anchorText,
      label,
    });
  };

  const handleWizardOtherClick = () => {
    if (!session.wizard) return;
    setNewHeadingError(null);
    setSession((s) => (s.wizard ? { ...s, wizard: { ...s.wizard, awaitingNewHeadingText: true } } : s));
  };

  /** Lets the lawyer back out of the new-heading text field without answering
   * the current question — picking "כותרת חדשה" was a dead end otherwise,
   * with no way to change their mind and pick an existing heading instead. */
  const handleWizardBackFromNewHeading = () => {
    if (!session.wizard) return;
    setNewHeadingError(null);
    setNewHeadingInput('');
    setSession((s) => (s.wizard ? { ...s, wizard: { ...s.wizard, awaitingNewHeadingText: false } } : s));
  };

  const handleNewHeadingTextSubmit = (text: string) => {
    if (!session.wizard) return;
    const headingText = text.trim();
    if (!headingText) {
      setNewHeadingError(copy.errorEmptyNewHeading);
      return;
    }
    const group = session.wizard.groups[session.wizard.currentIndex];
    if (!group) return;
    setNewHeadingError(null);
    advanceWizard({
      request_indices: group.request_indices,
      new_heading_text: headingText,
      label: headingText,
    });
  };

  const handleLocationSelect = (paragraphIndex: number, label: string) => {
    appendTurn('user', label);
    const ctx = session.analyzeResult?.ordering_context;

    if (shouldOfferOuterClauseMatch(ctx)) {
      appendTurn('assistant', ctx!.clarification_message);
      setSession((s) => ({
        ...s,
        selectedLocationIndex: paragraphIndex,
        selectedLocationLabel: label,
        chronological: false,
        step: 'awaiting_outer_clause_match',
      }));
      return;
    }

    const shouldAskChronological =
      !!session.analyzeResult?.has_clause_numbers ||
      !!ctx?.pasted_generic_numbering ||
      (session.analyzeResult?.client_requests.length ?? 0) > 1;

    setSession((s) => ({
      ...s,
      selectedLocationIndex: paragraphIndex,
      selectedLocationLabel: label,
      step: shouldAskChronological ? 'awaiting_chronological' : 'awaiting_format',
      orderMode: 'pasted',
    }));
    if (shouldAskChronological) {
      appendTurn('assistant', copy.askChronological);
    } else {
      appendTurn('assistant', copy.askFormat);
    }
  };

  const proceedToFormat = (orderMode: SkillOrderMode, notice?: string) => {
    if (notice) appendTurn('assistant', notice);
    setSession((s) => ({ ...s, orderMode, step: 'awaiting_format' }));
    appendTurn('assistant', copy.askFormat);
  };

  const handleChronologicalSelect = (yes: boolean) => {
    appendTurn('user', yes ? copy.chronologicalYes : copy.chronologicalNo);
    const ctx = session.analyzeResult?.ordering_context;

    if (!yes) {
      setSession((s) => ({ ...s, chronological: false, orderMode: 'pasted' }));
      if (shouldOfferOuterClauseMatch(ctx)) {
        appendTurn('assistant', ctx!.clarification_message);
        setSession((s) => ({ ...s, step: 'awaiting_outer_clause_match' }));
        return;
      }
      proceedToFormat('pasted');
      return;
    }

    setSession((s) => ({ ...s, chronological: true }));

    if (shouldOfferOuterClauseMatch(ctx)) {
      appendTurn('assistant', ctx!.clarification_message);
      setSession((s) => ({ ...s, step: 'awaiting_outer_clause_match' }));
      return;
    }

    proceedToFormat('generic_numbered');
  };

  const handleOuterClauseMatch = (yes: boolean) => {
    appendTurn('user', yes ? copy.askOuterClauseMatchYes : copy.askOuterClauseMatchNo);
    if (yes) {
      setSession((s) => ({ ...s, orderMode: 'outer_clause' }));
      proceedToFormat('outer_clause');
      return;
    }
    setSession((s) => ({ ...s, orderMode: 'append_at_location' }));
    proceedToFormat('append_at_location');
  };

  const handleFormatSelect = (mode: 'plain' | 'track_changes') => {
    appendTurn('user', mode === 'track_changes' ? copy.formatTrackChanges : copy.formatPlain);
    if (mode === 'track_changes') {
      setSession((s) => ({
        ...s,
        formatMode: mode,
        step: 'awaiting_author',
      }));
      appendTurn('assistant', copy.askAuthor);
    } else if (session.orderMode === 'auto' && session.strategyResult) {
      setSession((s) => ({ ...s, formatMode: mode }));
      void runExecuteConfirmed({ formatMode: mode, author: copy.defaultAuthor });
    } else {
      if (!session.analyzeResult || session.selectedLocationIndex == null) return;
      setSession((s) => ({ ...s, formatMode: mode }));
      void runExecute({
        formatMode: mode,
        author: copy.defaultAuthor,
        analyzeResult: session.analyzeResult,
        selectedLocationIndex: session.selectedLocationIndex,
        selectedLocationLabel: session.selectedLocationLabel ?? '',
        chronological: session.chronological ?? false,
        orderMode: session.orderMode,
      });
    }
  };

  const runExecuteWithAuthor = (author: string) => {
    if (session.orderMode === 'auto' && session.strategyResult) {
      void runExecuteConfirmed({ formatMode: 'track_changes', author });
      return;
    }
    if (!session.analyzeResult || session.selectedLocationIndex == null) return;
    void runExecute({
      formatMode: 'track_changes',
      author,
      analyzeResult: session.analyzeResult,
      selectedLocationIndex: session.selectedLocationIndex,
      selectedLocationLabel: session.selectedLocationLabel ?? '',
      chronological: session.chronological ?? false,
      orderMode: session.orderMode,
    });
  };

  const handleAuthorMyName = () => {
    const author = profileAuthorName.trim() || copy.defaultAuthor;
    appendTurn('user', copy.authorMyName(author));
    runExecuteWithAuthor(author);
  };

  const handleAuthorUnknown = () => {
    appendTurn('user', copy.authorUnknown);
    runExecuteWithAuthor(copy.defaultAuthor);
  };

  const handleAuthorSomeoneElse = () => {
    appendTurn('user', copy.authorSomeoneElse);
    setSession((s) => ({ ...s, step: 'awaiting_author_custom' }));
    appendTurn('assistant', copy.askCustomAuthor);
  };

  const handleCustomAuthorSubmit = (authorText: string) => {
    const author = authorText.trim() || copy.defaultAuthor;
    appendTurn('user', author);
    runExecuteWithAuthor(author);
  };

  const runExecute = async (params: {
    formatMode: 'plain' | 'track_changes';
    author: string;
    analyzeResult: SkillAnalyzeResult;
    selectedLocationIndex: number;
    selectedLocationLabel: string;
    chronological: boolean;
    orderMode: SkillOrderMode;
  }) => {
    if (executingRef.current) return;
    executingRef.current = true;
    setBusy(true);
    setSession((s) => ({
      ...s,
      step: 'executing',
      error: null,
      formatMode: params.formatMode,
      author: params.author,
    }));
    appendTurn('assistant', copy.installing);

    try {
      const { analyzeResult, selectedLocationIndex, chronological, formatMode, author, orderMode, selectedLocationLabel } = params;

      const paras = await getDocumentParagraphsForSkill();

      const res = await apiService.executeAgentSkill({
        skillId: skill.id,
        clientRequests: analyzeResult.client_requests,
        paragraphs: paras,
        locationParagraphIndex: selectedLocationIndex,
        chronological,
        formatMode,
        author,
        language: SKILL_AGENT_LANGUAGE,
        orderMode,
        outerClauseRefs: analyzeResult.ordering_context?.outer_clause_refs,
        locationLabel: selectedLocationLabel,
        locationOptions: analyzeResult.location_options,
      });

      if (!res.success || !res.data) {
        throw new Error(res.message || 'Execution failed');
      }

      const executeResult: SkillExecuteResult = {
        insertions: (res.data.insertions || []).map((ins) => ({
          client_request_text: ins.client_request_text,
          target_paragraph_index: ins.target_paragraph_index,
          insert_mode: ins.insert_mode as 'after' | 'before' | 'replace',
          format: ins.format as 'plain' | 'track_changes',
          author: ins.author,
          paragraph_role: ins.paragraph_role === 'heading' ? 'heading' : undefined,
        })),
        summary: res.data.summary || '',
        insertion_strategy: res.data.insertion_strategy,
        pending_placement: res.data.pending_placement ?? null,
        strategy_trace: res.data.strategy_trace || [],
      };

      const { succeeded, failed, details } =
        executeResult.insertions.length > 0
          ? await executeSkillInsertions(executeResult.insertions)
          : { succeeded: 0, failed: 0, details: [] as string[] };

      const pending = executeResult.pending_placement;
      const summary =
        succeeded > 0
          ? copy.doneSummary(succeeded, failed)
          : pending?.items.length
            ? '' // nothing confidently placed yet — the heading question covers it
            : copy.noneInserted;

      if (summary) {
        appendTurn('assistant', `${summary}\n\n${res.data.summary || ''}`);
      }

      // The placement-strategy agent loop (engine: _map_requests_to_sections_auto)
      // does NOT guess-then-offer-to-fix: a request it isn't confident about is
      // held back (not inserted) and the lawyer is asked WHICH EXISTING HEADING
      // it belongs under, with real headings from this document as examples —
      // once told, the agent places it (and picks the exact chronological spot
      // within that heading) without asking again.
      if (pending?.items.length) {
        appendTurn(
          'assistant',
          copy.askPendingHeading(
            pending.items.map((it) => it.text_preview),
            pending.available_headings.map((h) => h.label),
          ),
        );
        setSession((prev) => ({
          ...prev,
          step: 'awaiting_pending_heading',
          executeResult,
          author,
          formatMode,
          error: failed > 0 ? details.join('\n') : null,
        }));
      } else if (succeeded > 0) {
        // General post-execution safety net: ask even when nothing was flagged
        // low-confidence — the placement-strategy loop can still be wrong about
        // something it WAS confident about, and the lawyer is the one who
        // actually knows. Not asked when nothing succeeded (nothing to move).
        appendTurn('assistant', copy.askReposition);
        setSession((prev) => ({
          ...prev,
          step: 'awaiting_reposition_feedback',
          executeResult,
          author,
          formatMode,
          error: failed > 0 ? details.join('\n') : null,
        }));
      } else {
        setSession((prev) => ({
          ...prev,
          step: 'done',
          executeResult,
          author,
          formatMode,
          error: failed > 0 ? details.join('\n') : null,
        }));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Execution failed';
      appendTurn('assistant', copy.errorExecute(msg));
      setSession((prev) => ({ ...prev, step: 'done', error: msg }));
    } finally {
      executingRef.current = false;
      setBusy(false);
    }
  };

  const handlePendingHeadingChoice = async (
    headingParagraphIndex: number,
    headingLabel: string,
    anchorText: string,
  ) => {
    const pending = session.executeResult?.pending_placement;
    if (!pending?.items.length) return;
    appendTurn('user', headingLabel);
    appendTurn('assistant', copy.placingPending);
    setBusy(true);
    setSession((s) => ({ ...s, step: 'placing_pending' }));
    try {
      const paras = await getDocumentParagraphsForSkill();
      const res = await apiService.placePendingAgentSkill({
        skillId: skill.id,
        pendingItems: pending.items.map((it) => ({ index: it.index, text: it.text })),
        headingParagraphIndex,
        headingLabel,
        anchorText,
        paragraphs: paras,
        formatMode: session.formatMode ?? 'plain',
        author: session.author,
        language: SKILL_AGENT_LANGUAGE,
      });

      if (!res.success || !res.data) {
        throw new Error(res.message || 'Placement failed');
      }

      const placedInsertions = (res.data.insertions || []).map((ins) => ({
        client_request_text: ins.client_request_text,
        target_paragraph_index: ins.target_paragraph_index,
        insert_mode: ins.insert_mode as 'after' | 'before' | 'replace',
        format: ins.format as 'plain' | 'track_changes',
        author: ins.author,
      }));
      const { succeeded, failed, details } = await executeSkillInsertions(placedInsertions);

      appendTurn(
        'assistant',
        succeeded > 0 ? copy.pendingPlacedDone(pending.items.length, headingLabel) : copy.noneInserted,
      );
      if (succeeded > 0) {
        appendTurn('assistant', copy.askReposition);
      }

      setSession((prev) => ({
        ...prev,
        step: succeeded > 0 ? 'awaiting_reposition_feedback' : 'done',
        executeResult: prev.executeResult
          ? {
              ...prev.executeResult,
              insertions: [...prev.executeResult.insertions, ...placedInsertions],
              pending_placement: null,
            }
          : prev.executeResult,
        error: failed > 0 ? details.join('\n') : null,
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Placement failed';
      appendTurn('assistant', copy.errorPlacePending(msg));
      setSession((s) => ({ ...s, step: 'awaiting_pending_heading', error: msg }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Automatic LLM-based verification of THIS call's own insertions, run right
   * after they land — no lawyer instruction, the model inspects the actual
   * resulting document on its own. The chronological placement itself is
   * regex/parsing based (parse_clause_sort_key, paragraph_primary_outer_clause,
   * chronological_anchor_for_group_item — fast, deterministic) but blind to
   * anything that needs genuine content understanding, most notably an
   * existing clause spanning an unnumbered CONTINUATION paragraph (e.g. "1.
   * סעיף 1א'- ..." followed by "בסיפא הסעיף יתווסף: ...", still part of the
   * same clause) — regex cannot reliably tell that apart from an unrelated
   * trailing paragraph that also happens to lack its own clause number, so a
   * new item can land INSIDE an existing clause's own declaration instead of
   * after all of it. This call gives that specific judgment to the model.
   * Best-effort and fails silently (logged, not surfaced) — a verification
   * step failing must never cast doubt on an insertion that already
   * succeeded; the manual "anything to reposition?" safety net right after
   * this call is the backstop for whatever it misses.
   */
  const runSelfCheckAndFix = async (executeResult: SkillExecuteResult) => {
    if (!executeResult.insertions.length) return;
    try {
      appendTurn('assistant', copy.selfChecking);
      const freshParas = await getDocumentParagraphsForSkill();
      const res = await apiService.selfCheckAgentSkillPlacement({
        skillId: skill.id,
        recentInsertions: executeResult.insertions.map((ins) => ({
          client_request_text: ins.client_request_text,
          target_paragraph_index: ins.target_paragraph_index,
          format: ins.format,
          author: ins.author,
        })),
        paragraphs: freshParas,
        language: SKILL_AGENT_LANGUAGE,
      });

      const moves = res.success ? res.data?.moves ?? [] : [];
      if (!moves.length) {
        appendTurn('assistant', copy.selfCheckAllCorrect);
        return;
      }

      const labels = moves.map((m) => {
        const text = m.match_text.trim();
        return text.length > 40 ? `${text.slice(0, 40)}…` : text;
      });
      appendTurn('assistant', copy.selfCheckFoundIssues(labels));

      const moveResult = await repositionSkillInsertions(moves);
      if (moveResult.succeeded > 0) {
        appendTurn('assistant', copy.selfCheckFixed(moveResult.succeeded));
      }
    } catch (e) {
      // Fail silently — this is a best-effort safety net, not part of the
      // insertion itself; log for debugging, don't alarm the lawyer over it.
      logError('[SkillRunner] self-check placement failed', e);
    }
  };

  /**
   * Auto-mode counterpart to runExecute: the lawyer already answered every
   * wizard question (session.wizard.assignments), so this just applies the
   * agreed per-group assignments — no more LLM calls needed for the mapping.
   * Every group was resolved by the wizard (existing heading or a brand-new
   * one), so there is nothing left to defer — the ask-heading-first flow
   * (handlePendingHeadingChoice) only ever applies to the separate manual
   * single-location execute() path, not this one.
   */
  const runExecuteConfirmed = async (params: { formatMode: 'plain' | 'track_changes'; author: string }) => {
    if (executingRef.current) return;
    if (!session.analyzeResult || !session.strategyResult) return;
    executingRef.current = true;
    setBusy(true);
    setSession((s) => ({
      ...s,
      step: 'executing',
      error: null,
      formatMode: params.formatMode,
      author: params.author,
    }));
    appendTurn('assistant', copy.installing);

    try {
      const { analyzeResult, strategyResult, wizard } = session;
      const paras = await getDocumentParagraphsForSkill();

      const res = await apiService.executeConfirmedAgentSkill({
        skillId: skill.id,
        clientRequests: analyzeResult.client_requests,
        groupAssignments: wizard?.assignments ?? [],
        availableHeadings: strategyResult.available_headings,
        paragraphs: paras,
        formatMode: params.formatMode,
        author: params.author,
        language: SKILL_AGENT_LANGUAGE,
      });

      if (!res.success || !res.data) {
        throw new Error(res.message || 'Execution failed');
      }

      const executeResult: SkillExecuteResult = {
        insertions: (res.data.insertions || []).map((ins) => ({
          client_request_text: ins.client_request_text,
          target_paragraph_index: ins.target_paragraph_index,
          insert_mode: ins.insert_mode as 'after' | 'before' | 'replace',
          format: ins.format as 'plain' | 'track_changes',
          author: ins.author,
          paragraph_role: ins.paragraph_role === 'heading' ? 'heading' : undefined,
        })),
        summary: res.data.summary || '',
        pending_placement: null,
        strategy_trace: strategyResult.strategy_trace || [],
      };

      // A brand-new heading in this batch must copy an EXISTING heading's
      // real paragraph style/list structure, not whatever happens to be
      // adjacent to it in the insertion run (see
      // AgentDocumentInserter.applyHeadingStructureToInsertedText for why
      // that generic proximity copy silently turned the new heading into a
      // numbered clause). Same "last heading" the engine itself anchors a new
      // heading after (`max(available_headings, key=paragraph_index)`), so
      // its own text is a genuine heading paragraph to copy structure from.
      const headingReferenceText = strategyResult.available_headings.length
        ? strategyResult.available_headings.reduce((a, b) =>
            a.paragraph_index > b.paragraph_index ? a : b,
          ).anchor_text
        : undefined;

      const { succeeded, failed, details } =
        executeResult.insertions.length > 0
          ? await executeSkillInsertions(executeResult.insertions, headingReferenceText)
          : { succeeded: 0, failed: 0, details: [] as string[] };

      const summary = succeeded > 0 ? copy.doneSummary(succeeded, failed) : copy.noneInserted;
      appendTurn('assistant', `${summary}\n\n${res.data.summary || ''}`);

      if (succeeded > 0) {
        // LLM self-check: the chronological placement above is regex/parsing
        // based (fast, deterministic) but blind to anything that needs real
        // content understanding — e.g. an existing clause spanning an
        // unnumbered continuation paragraph, which regex cannot reliably tell
        // apart from an unrelated trailing paragraph. Runs BEFORE the manual
        // reposition safety net below, so the lawyer sees an already-corrected
        // result rather than having to catch this class of bug themselves.
        await runSelfCheckAndFix(executeResult);

        // General post-execution safety net: ask even when nothing was flagged
        // low-confidence — the placement-strategy loop can still be wrong about
        // something it WAS confident about, and the lawyer is the one who
        // actually knows. Not asked when nothing succeeded (nothing to move).
        appendTurn('assistant', copy.askReposition);
        setSession((prev) => ({
          ...prev,
          step: 'awaiting_reposition_feedback',
          executeResult,
          author: params.author,
          formatMode: params.formatMode,
          error: failed > 0 ? details.join('\n') : null,
        }));
      } else {
        setSession((prev) => ({
          ...prev,
          step: 'done',
          executeResult,
          author: params.author,
          formatMode: params.formatMode,
          error: failed > 0 ? details.join('\n') : null,
        }));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Execution failed';
      appendTurn('assistant', copy.errorExecute(msg));
      setSession((prev) => ({ ...prev, step: 'done', error: msg }));
    } finally {
      executingRef.current = false;
      setBusy(false);
    }
  };

  const handleRepositionChoice = (yes: boolean) => {
    appendTurn('user', yes ? copy.repositionYes : copy.repositionNo);
    if (!yes) {
      setSession((s) => ({ ...s, step: 'done' }));
      return;
    }
    appendTurn('assistant', copy.askRepositionInstruction);
    setSession((s) => ({ ...s, step: 'awaiting_reposition_instruction' }));
  };

  const handleRepositionInstructionSubmit = async (instruction: string) => {
    if (!session.executeResult?.insertions?.length) return;
    setBusy(true);
    appendTurn('user', instruction);
    appendTurn('assistant', copy.repositioning);
    try {
      const paras = await getDocumentParagraphsForSkill();
      const res = await apiService.repositionAgentSkill({
        skillId: skill.id,
        instruction,
        recentInsertions: session.executeResult.insertions.map((ins) => ({
          client_request_text: ins.client_request_text,
          target_paragraph_index: ins.target_paragraph_index,
          format: ins.format,
          author: ins.author,
        })),
        paragraphs: paras,
        language: SKILL_AGENT_LANGUAGE,
      });

      if (!res.success || !res.data || !res.data.moves.length) {
        appendTurn('assistant', copy.repositionNone);
        setSession((s) => ({ ...s, step: 'awaiting_reposition_instruction' }));
        return;
      }

      const moveResult = await repositionSkillInsertions(res.data.moves);
      if (moveResult.succeeded > 0) {
        appendTurn('assistant', copy.repositionDone(moveResult.succeeded));
      } else {
        appendTurn('assistant', copy.errorReposition(moveResult.details.join('\n') || 'unknown'));
      }
      appendTurn('assistant', copy.askRepositionAgain);
      setSession((s) => ({ ...s, step: 'awaiting_reposition_feedback' }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Reposition failed';
      appendTurn('assistant', copy.errorReposition(msg));
      setSession((s) => ({ ...s, step: 'awaiting_reposition_feedback' }));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;

    switch (session.step) {
      case 'awaiting_paste':
        await handlePasteSubmit(trimmed);
        break;
      case 'awaiting_author_custom':
        handleCustomAuthorSubmit(trimmed);
        break;
      case 'awaiting_reposition_instruction':
        await handleRepositionInstructionSubmit(trimmed);
        break;
      default:
        break;
    }
  };

  // The wizard (awaiting_wizard_question) never uses the shared bottom
  // composer, including for its new-heading free text — that input lives
  // inline in the wizard card itself (see the render block below), which is
  // the point of this component: the whole interaction stays inside one
  // step-through card instead of round-tripping through the chat composer.
  const showComposer =
    session.step === 'awaiting_paste' ||
    session.step === 'awaiting_author_custom' ||
    session.step === 'awaiting_reposition_instruction';

  const composerPlaceholder =
    session.step === 'awaiting_author_custom'
      ? copy.placeholderAuthor
      : session.step === 'awaiting_reposition_instruction'
        ? copy.placeholderReposition
        : copy.placeholderPaste;

  const composerFollowUp = session.turns.some((turn) => turn.role === 'user');

  const myNameLabel = copy.authorMyName(profileAuthorName.trim() || copy.defaultAuthor);

  const locationOptions = session.analyzeResult?.location_options ?? [];

  return (
    <div className="skill-runner">
      <div className="skill-runner-header">
        <button type="button" className="skill-runner-back" onClick={onBack} disabled={busy}>
          <ArrowLeft size={14} aria-hidden />
          Workflows
        </button>
        <span className="skill-runner-title">{displaySkill.name}</span>
      </div>

      <div className="skill-runner-thread">
        {session.turns.map((turn) => (
          <div
            key={turn.id}
            className={`skill-runner-turn skill-runner-turn--${turn.role}`}
            dir="auto"
          >
            {turn.role === 'assistant' && <Bot size={14} className="skill-runner-turn-icon" aria-hidden />}
            <div className="skill-runner-turn-content">{turn.content}</div>
          </div>
        ))}

        {session.step === 'awaiting_location' && locationOptions.length > 0 && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAutoLocationSelect}
            >
              {copy.decideYourself}
            </button>
            {locationOptions.map((opt) => (
              <button
                key={`${opt.paragraph_index}-${opt.label}`}
                type="button"
                className="skill-runner-option-btn"
                disabled={busy}
                onClick={() => handleLocationSelect(opt.paragraph_index, opt.label)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {session.step === 'awaiting_chronological' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleChronologicalSelect(true)}
            >
              {copy.chronologicalYes}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleChronologicalSelect(false)}
            >
              {copy.chronologicalNo}
            </button>
          </div>
        )}

        {session.step === 'awaiting_outer_clause_match' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleOuterClauseMatch(true)}
            >
              {copy.askOuterClauseMatchYes}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleOuterClauseMatch(false)}
            >
              {copy.askOuterClauseMatchNo}
            </button>
          </div>
        )}

        {session.step === 'awaiting_format' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleFormatSelect('track_changes')}
            >
              {copy.formatTrackChanges}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleFormatSelect('plain')}
            >
              {copy.formatPlain}
            </button>
          </div>
        )}

        {session.step === 'awaiting_author' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAuthorMyName}
            >
              {myNameLabel}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAuthorUnknown}
            >
              {copy.authorUnknown}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={handleAuthorSomeoneElse}
            >
              {copy.authorSomeoneElse}
            </button>
          </div>
        )}

        {session.step === 'awaiting_wizard_question' && session.wizard && (
          <div className="skill-runner-wizard-card" dir="auto">
            <div className="skill-runner-wizard-progress">
              {copy.wizardProgress(session.wizard.currentIndex + 1, session.wizard.groups.length)}
            </div>
            <div className="skill-runner-wizard-question">
              {wizardQuestionText(copy, session.wizard.groups[session.wizard.currentIndex])}
            </div>
            {!session.wizard.awaitingNewHeadingText ? (
              <div className="skill-runner-wizard-options">
                {session.wizard.availableHeadings.map((h) => (
                  <button
                    key={`${h.paragraph_index}-${h.label}`}
                    type="button"
                    className="skill-runner-option-btn"
                    disabled={busy}
                    onClick={() => handleWizardHeadingChoice(h.paragraph_index, h.label, h.anchor_text)}
                  >
                    {h.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="skill-runner-option-btn"
                  disabled={busy}
                  onClick={handleWizardOtherClick}
                >
                  {copy.wizardOptionOther}
                </button>
              </div>
            ) : (
              <form
                className="skill-runner-wizard-input-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleNewHeadingTextSubmit(newHeadingInput);
                  setNewHeadingInput('');
                }}
              >
                <div className="skill-runner-wizard-input-label">{copy.askNewHeadingText}</div>
                <div className="skill-runner-wizard-input-controls">
                  <input
                    type="text"
                    className="skill-runner-wizard-input"
                    value={newHeadingInput}
                    onChange={(e) => setNewHeadingInput(e.target.value)}
                    placeholder={copy.placeholderNewHeading}
                    disabled={busy}
                    dir="auto"
                    autoFocus
                  />
                  <button type="submit" className="skill-runner-wizard-submit-btn" disabled={busy}>
                    {copy.wizardSubmit}
                  </button>
                </div>
                {newHeadingError && <div className="skill-runner-wizard-input-error">{newHeadingError}</div>}
                <button
                  type="button"
                  className="skill-runner-wizard-back-btn"
                  disabled={busy}
                  onClick={handleWizardBackFromNewHeading}
                >
                  {copy.wizardBackToOptions}
                </button>
              </form>
            )}
          </div>
        )}

        {session.step === 'awaiting_pending_heading' && (
          <div className="skill-runner-options">
            {(session.executeResult?.pending_placement?.available_headings ?? []).map((h) => (
              <button
                key={`${h.paragraph_index}-${h.label}`}
                type="button"
                className="skill-runner-option-btn"
                disabled={busy}
                onClick={() => void handlePendingHeadingChoice(h.paragraph_index, h.label, h.anchor_text)}
              >
                {h.label}
              </button>
            ))}
          </div>
        )}

        {session.step === 'awaiting_reposition_feedback' && (
          <div className="skill-runner-options">
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleRepositionChoice(true)}
            >
              {copy.repositionYes}
            </button>
            <button
              type="button"
              className="skill-runner-option-btn"
              disabled={busy}
              onClick={() => handleRepositionChoice(false)}
            >
              {copy.repositionNo}
            </button>
          </div>
        )}

        {busy && (
          <div className="skill-runner-busy">
            <RefreshCw size={12} className="spinning" aria-hidden />
            <span>Working…</span>
          </div>
        )}

        {session.error && session.step === 'done' && (
          <div className="skill-runner-error">{session.error}</div>
        )}

        <div ref={scrollRef} />
      </div>

      {showComposer && (
        <div className="skill-runner-composer">
          <ChatComposer
            value={input}
            onChange={setInput}
            onSubmit={async ({ text }) => {
              await handleSubmit(text);
              setInput('');
            }}
            disabled={busy}
            submitting={busy}
            placeholder={composerPlaceholder}
            layout="sticky"
            followUp={composerFollowUp}
            submitTitle="Send"
            showAgentModeSelector={false}
          />
        </div>
      )}
    </div>
  );
};
